window.BryghtTracking = window.BryghtTracking || {};

const state = window.BryghtTracking;
const ATTRIBUTION_QUERY_PARAM_PATTERN = /^(utm_[a-z0-9_]+|gclid|gbraid|wbraid|fbclid)$/i;
const SENSITIVE_QUERY_PARAM_PATTERN = /(^|_)(client_secret|secret|token|password|otp)$|^(payment_intent|setup_intent|redirect_status|key|email|email_address|session_id)$/i;

state.config = state.config || {};
state.consent = state.consent || {};
state.initializedGoogleTagIds = state.initializedGoogleTagIds || new Set();
state.initializedMetaPixelIds = state.initializedMetaPixelIds || new Set();
state.trackedEventKeys = state.trackedEventKeys || new Set();
state.lastPageViewKey = state.lastPageViewKey || null;
state.lastConfigureKey = state.lastConfigureKey || null;
state.googleJsInitialized = state.googleJsInitialized || false;

function readConfig() {
  const element = document.getElementById("bryght-tracking-config");
  if (!element?.textContent) return { events: [] };

  try {
    const config = JSON.parse(element.textContent);
    return {
      communityId: config.communityId,
      googleTagId: isValidGoogleTagId(config.googleTagId) ? config.googleTagId : null,
      googleAdsPurchaseConversionSendTo: isValidGoogleAdsConversionSendTo(config.googleAdsPurchaseConversionSendTo) ? config.googleAdsPurchaseConversionSendTo : null,
      metaPixelId: isValidMetaPixelId(config.metaPixelId) ? config.metaPixelId : null,
      events: Array.isArray(config.events) ? config.events : [],
    };
  } catch {
    return { events: [] };
  }
}

function configForCurrentPage(config) {
  if (!currentUrlHasSensitiveQueryParams()) return config;

  return {
    ...config,
    googleTagId: null,
    googleAdsPurchaseConversionSendTo: null,
    metaPixelId: null,
  };
}

function trackingAllowed() {
  return state.consent.marketing !== "denied";
}

function loadScript(id, src) {
  if (document.getElementById(id)) return;

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function currentTrackingPageUrl() {
  try {
    const url = new URL(window.location.href);
    const allowedParams = new URLSearchParams();

    url.searchParams.forEach((value, key) => {
      if (ATTRIBUTION_QUERY_PARAM_PATTERN.test(key)) allowedParams.append(key, value);
    });

    url.search = allowedParams.toString();
    url.hash = "";

    return url.toString();
  } catch {
    return `${window.location.origin}${window.location.pathname}`;
  }
}

function currentTrackingPagePath(pageUrl) {
  try {
    const url = new URL(pageUrl);
    return `${url.pathname}${url.search}`;
  } catch {
    return window.location.pathname;
  }
}

function currentUrlHasSensitiveQueryParams() {
  try {
    const url = new URL(window.location.href);

    return Array.from(url.searchParams.keys()).some((key) => SENSITIVE_QUERY_PARAM_PATTERN.test(key));
  } catch {
    return true;
  }
}

function initializeGoogleTag(googleTagId) {
  if (!isValidGoogleTagId(googleTagId)) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };

  loadScript(
    `bryght-google-tag-${googleTagId}`,
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleTagId)}`
  );

  if (!state.googleJsInitialized) {
    window.gtag("js", new Date());
    state.googleJsInitialized = true;
  }

  if (state.initializedGoogleTagIds.has(googleTagId)) return;

  window.gtag("config", googleTagId, { send_page_view: false });
  state.initializedGoogleTagIds.add(googleTagId);
}

function initializeMetaPixel(metaPixelId) {
  if (!isValidMetaPixelId(metaPixelId)) return;

  if (!window.fbq) {
    const fbq = function () {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };

    window.fbq = fbq;
    window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];

    loadScript("bryght-meta-pixel", "https://connect.facebook.net/en_US/fbevents.js");
  }

  if (state.initializedMetaPixelIds.has(metaPixelId)) return;

  window.fbq("init", metaPixelId);
  state.initializedMetaPixelIds.add(metaPixelId);
}

function trackPageView() {
  const { communityId, googleTagId, metaPixelId } = state.config;
  const event = findTrackingEvent("page_view");
  const pageLocation = currentTrackingPageUrl();
  const pagePath = currentTrackingPagePath(pageLocation);
  const pageKey = [communityId || "", googleTagId || "", metaPixelId || "", pageLocation].join("|");
  if (state.lastPageViewKey === pageKey) return;

  let tracked = false;

  if (isValidGoogleTagId(googleTagId) && window.gtag) {
    window.gtag("event", "page_view", {
      send_to: googleTagId,
      event_id: event?.event_id,
      page_path: pagePath,
      page_location: pageLocation,
      page_title: document.title,
    });
    tracked = true;
  }

  if (isValidMetaPixelId(metaPixelId) && window.fbq) {
    window.fbq("trackSingle", metaPixelId, "PageView", { page_location: pageLocation }, { eventID: event?.event_id });
    tracked = true;
  }

  if (tracked) state.lastPageViewKey = pageKey;
}

function trackCheckoutEvent() {
  findTrackingEvents("begin_checkout").forEach(trackSingleCheckoutEvent);
}

function trackSingleCheckoutEvent(event) {
  const checkoutEventId = event?.event_id;

  if (!checkoutEventId) return;

  const contentIds = parseEventArray(event.content_ids);
  const numItems = event.num_items === undefined ? undefined : parseEventInteger(event.num_items);
  const value = parseEventFloat(event.value);
  const eventData = compactObject({
    value,
    currency: event.currency,
    content_ids: contentIds,
    content_name: event.content_name,
    content_type: event.content_type,
    subscription_period: event.subscription_period,
    coupon: event.coupon,
    num_items: numItems,
  });

  trackGoogleEvent("google", state.config.googleTagId, "begin_checkout", checkoutEventId, {
    send_to: state.config.googleTagId,
    event_id: checkoutEventId,
    value,
    currency: event.currency,
    subscription_period: event.subscription_period,
    coupon: event.coupon,
    items: compactItems([
      {
        item_id: contentIds[0],
        item_name: event.content_name,
        item_category: event.content_type,
        quantity: numItems,
      },
    ]),
  });
  trackMetaEvent("meta", state.config.metaPixelId, "begin_checkout", checkoutEventId, "InitiateCheckout", eventData);
}

function trackViewContentEvent() {
  findTrackingEvents("view_content").forEach(trackSingleViewContentEvent);
}

function trackSingleViewContentEvent(event) {
  const viewContentEventId = event?.event_id;

  if (!viewContentEventId) return;

  const contentIds = parseEventArray(event.content_ids);
  const numItems = event.num_items === undefined ? undefined : parseEventInteger(event.num_items);
  const value = parseEventFloat(event.value);
  const eventData = compactObject({
    value,
    currency: event.currency,
    content_ids: contentIds,
    content_name: event.content_name,
    content_type: event.content_type,
    subscription_period: event.subscription_period,
    coupon: event.coupon,
    num_items: numItems,
  });

  trackGoogleEvent("google", state.config.googleTagId, "view_content", viewContentEventId, {
    send_to: state.config.googleTagId,
    event_id: viewContentEventId,
    value,
    currency: event.currency,
    subscription_period: event.subscription_period,
    coupon: event.coupon,
    items: compactItems([
      {
        item_id: contentIds[0],
        item_name: event.content_name,
        item_category: event.content_type,
        quantity: numItems,
      },
    ]),
  }, "view_item");
  trackMetaEvent("meta", state.config.metaPixelId, "view_content", viewContentEventId, "ViewContent", eventData);
}

function trackPurchaseEvent() {
  findTrackingEvents("purchase").forEach(trackSinglePurchaseEvent);
}

function trackSinglePurchaseEvent(event) {
  const purchaseEventId = event?.event_id;

  if (!purchaseEventId) return;

  const contentIds = parseEventArray(event.content_ids);
  const numItems = event.num_items === undefined ? undefined : parseEventInteger(event.num_items);
  const eventData = compactObject({
    value: parseEventFloat(event.value),
    currency: event.currency,
    content_ids: contentIds,
    content_name: event.content_name,
    content_type: event.content_type,
    subscription_period: event.subscription_period,
    coupon: event.coupon,
    num_items: numItems,
  });

  trackGoogleEvent("google", state.config.googleTagId, "purchase", purchaseEventId, {
    send_to: state.config.googleTagId,
    event_id: purchaseEventId,
    transaction_id: event.transaction_id,
    value: eventData.value,
    currency: eventData.currency,
    subscription_period: event.subscription_period,
    coupon: event.coupon,
    items: compactItems([
      {
        item_id: contentIds[0],
        item_name: event.content_name,
        item_category: event.content_type,
        quantity: eventData.num_items,
      },
    ]),
  });
  trackGoogleAdsPurchaseConversion(event, eventData);
  trackMetaEvent("meta", state.config.metaPixelId, "purchase", purchaseEventId, "Purchase", eventData);
}

function trackGoogleAdsPurchaseConversion(event, eventData) {
  const sendTo = state.config.googleAdsPurchaseConversionSendTo;
  const eventId = event?.event_id;

  if (!eventId) return false;
  if (!isValidGoogleAdsConversionSendTo(sendTo)) return false;
  if (!Number.isFinite(eventData.value) || !eventData.currency || !event.transaction_id) return false;

  return trackGoogleEvent("google_ads", sendTo, "purchase_conversion", eventId, {
    send_to: sendTo,
    value: eventData.value,
    currency: eventData.currency,
    transaction_id: event.transaction_id,
  }, "conversion");
}

function trackRegistrationEvent() {
  findTrackingEvents("complete_registration").forEach(trackSingleRegistrationEvent);
}

function trackSingleRegistrationEvent(event) {
  const registrationEventId = event?.event_id;

  if (!registrationEventId) return;

  const eventData = compactObject({ method: event.method });
  trackGoogleEvent("google", state.config.googleTagId, "complete_registration", registrationEventId, {
    send_to: state.config.googleTagId,
    event_id: registrationEventId,
    method: event.method,
  }, "sign_up");
  trackMetaEvent(
    "meta",
    state.config.metaPixelId,
    "complete_registration",
    registrationEventId,
    "CompleteRegistration",
    eventData
  );
}

function trackGoogleEvent(vendor, destinationId, eventName, eventId, payload, mappedName = eventName) {
  if (!isValidGoogleDestinationId(vendor, destinationId) || !window.gtag) return false;
  if (alreadyTrackedEvent(vendor, destinationId, eventName, eventId)) return false;

  window.gtag("event", mappedName, compactObject(payload));
  markTrackedEvent(vendor, destinationId, eventName, eventId);
  return true;
}

function trackMetaEvent(vendor, destinationId, eventName, eventId, mappedName, payload) {
  if (!isValidMetaPixelId(destinationId) || !window.fbq) return false;
  if (alreadyTrackedEvent(vendor, destinationId, eventName, eventId)) return false;

  window.fbq("trackSingle", destinationId, mappedName, compactObject(payload), { eventID: eventId });
  markTrackedEvent(vendor, destinationId, eventName, eventId);
  return true;
}

function findTrackingEvent(name) {
  return state.config.events?.find((event) => event?.name === name);
}

function findTrackingEvents(name) {
  return (state.config.events || []).filter((event) => event?.name === name);
}

function alreadyTrackedEvent(vendor, destinationId, eventName, eventId) {
  const key = trackedEventStorageKey(vendor, destinationId, eventName, eventId);
  if (state.trackedEventKeys.has(key)) return true;

  try {
    return window.sessionStorage?.getItem(key) === "1";
  } catch {
    return false;
  }
}

function markTrackedEvent(vendor, destinationId, eventName, eventId) {
  const key = trackedEventStorageKey(vendor, destinationId, eventName, eventId);
  state.trackedEventKeys.add(key);

  try {
    window.sessionStorage?.setItem(key, "1");
  } catch {
    // Ignore browsers or privacy modes that block sessionStorage.
  }
}

function trackedEventStorageKey(vendor, destinationId, eventName, eventId) {
  return [
    "bryght",
    "tracking",
    state.config.communityId || "unknown-community",
    vendor,
    destinationId,
    eventName,
    eventId,
  ].join(":");
}

function isValidGoogleTagId(value) {
  return /^(G-[A-Z0-9]+|GT-[A-Z0-9]+|AW-\d+)$/i.test(value || "");
}

function isValidGoogleDestinationId(vendor, value) {
  if (vendor === "google_ads") return isValidGoogleAdsConversionSendTo(value);

  return isValidGoogleTagId(value);
}

function isValidGoogleAdsConversionSendTo(value) {
  return /^AW-\d+\/[A-Za-z0-9_-]{1,128}$/.test(value || "");
}

function isValidMetaPixelId(value) {
  return /^\d{5,30}$/.test(value || "");
}

function parseEventArray(value) {
  return Array.isArray(value) ? value : [];
}

function parseEventFloat(value) {
  if (value === undefined || value === null || value === "") return undefined;

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function parseEventInteger(value) {
  if (value === undefined || value === null || value === "") return undefined;

  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function compactObject(object) {
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

function compactItems(items) {
  return items.map(compactObject).filter((item) => Object.keys(item).length > 0);
}

function configureTracking() {
  const config = configForCurrentPage(readConfig());
  state.config = config;

  if (!trackingAllowed()) return;
  if (!config.googleTagId && !config.metaPixelId) return;

  const configureKey = trackingConfigureKey(config);
  if (state.lastConfigureKey === configureKey) return;

  state.lastConfigureKey = configureKey;

  initializeGoogleTag(config.googleTagId);
  initializeMetaPixel(config.metaPixelId);
  trackPageView();
  trackViewContentEvent();
  trackCheckoutEvent();
  trackRegistrationEvent();
  trackPurchaseEvent();
}

document.addEventListener("turbo:load", configureTracking);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", configureTracking, { once: true });
} else {
  configureTracking();
}

function trackingConfigureKey(config) {
  return [
    currentTrackingPageUrl(),
    config.communityId || "",
    config.googleTagId || "",
    config.googleAdsPurchaseConversionSendTo || "",
    config.metaPixelId || "",
    JSON.stringify(config.events || []),
  ].join("|");
}
